/**
 * Browser upload engine (ARCHITECTURE §5.2 "Uploads & import", §12 "Upload").
 *
 *   const task = new UploadTask({ projectId, files, onProgress });
 *   const result = await task.promise;   // UploadResult {images, jobs}
 *   task.abort();                        // cancel + DELETE the upload (staged data removed)
 *   task.pause();                        // stop, keep the session (resume later)
 *
 * * Files are cut into `session.block_size` blocks (8 MiB); block ids are
 *   `btoa(zero-padded 6-digit index)`; {@link UploadTask} sends 4 blocks in parallel
 *   across all files of the upload.
 * * Strategies (from `UploadSession.strategy`): `local` → `PUT /api/uploads/{uid}/files/{idx}/blocks/{n}`;
 *   `azure` → `PUT put_url + "&comp=block&blockid=" + encodeURIComponent(id)` straight to Blob
 *   Storage (the server commits the block list in `/complete`).
 * * Failed blocks are retried with exponential backoff (network errors, 408/429/5xx; an
 *   Azure 403 first fetches a fresh SAS via `POST …/sas`).
 * * Resume after reload: the upload id and the file fingerprints (name, size, mtime) are
 *   kept in `localStorage`; selecting the same files again finds the record, lists the
 *   already staged blocks (`GET …/blocks`) and sends only the missing ones.
 */
import { api, ApiError, busyRetryDelayMs, isApiError, isBusyError } from './client';
import type { UploadOptions, UploadResult, UploadSession, UploadStrategy } from './types';

// --------------------------------------------------------------------------- blocks

/** Wire id of block `n`: base64 of the zero-padded 6-digit index (`btoa("000123")`). */
export function blockId(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n >= 1_000_000) throw new RangeError(`block index ${n} out of range`);
  return btoa(String(n).padStart(6, '0'));
}

export function blockCount(size: number, blockSize: number): number {
  return Math.ceil(size / blockSize);
}

/** Byte range `[start, end)` of block `n`. */
export function blockRange(n: number, size: number, blockSize: number): [number, number] {
  const start = n * blockSize;
  return [start, Math.min(size, start + blockSize)];
}

/** Every block id of a file, in order (the `/complete` block list). */
export function blockIds(nBlocks: number): string[] {
  return Array.from({ length: nBlocks }, (_, n) => blockId(n));
}

// --------------------------------------------------------------------------- ENVI pairing

/** Data-file extensions an ENVI header pairs with (mirrors `hsi/readers/envi.py`). */
export const ENVI_DATA_EXTENSIONS = [
  'img',
  'dat',
  'raw',
  'bil',
  'bip',
  'bsq',
  'hyspex',
  'bin',
  'sli',
] as const;

function basename(name: string): string {
  return name.replace(/\\/g, '/').split('/').pop() ?? name;
}

/** Candidate data file names (lower-case) of an ENVI header, in priority order. */
export function dataFileCandidates(headerName: string): string[] {
  const name = basename(headerName);
  if (!name.toLowerCase().endsWith('.hdr')) return [];
  const stem = name.slice(0, -4);
  const dot = stem.lastIndexOf('.');
  const ext = dot > 0 ? stem.slice(dot + 1).toLowerCase() : '';
  if ((ENVI_DATA_EXTENSIONS as readonly string[]).includes(ext)) return [stem.toLowerCase()];
  return [stem.toLowerCase(), ...ENVI_DATA_EXTENSIONS.map((e) => `${stem}.${e}`.toLowerCase())];
}

export interface FileGroups {
  /** Index groups, one per image (an ENVI pair is `[header, data]`), by first appearance. */
  groups: number[][];
  /** ENVI headers without their data file (the server rejects these). */
  orphanHeaders: number[];
}

/** How the server will group `names` into images (§5.2): `.hdr` + data file = one image. */
export function groupFiles(names: readonly string[]): FileGroups {
  const lower = names.map((n) => basename(n).toLowerCase());
  const position = new Map<string, number>();
  lower.forEach((name, i) => {
    if (!position.has(name)) position.set(name, i);
  });
  const used = new Set<number>();
  const groups: number[][] = [];
  const orphanHeaders: number[] = [];
  lower.forEach((name, i) => {
    if (!name.endsWith('.hdr')) return;
    const match = dataFileCandidates(names[i])
      .map((c) => position.get(c))
      .find((p) => p !== undefined && !used.has(p) && p !== i);
    if (match === undefined) {
      orphanHeaders.push(i);
      return;
    }
    groups.push([i, match]);
    used.add(i).add(match);
  });
  names.forEach((_, i) => {
    if (!used.has(i) && !orphanHeaders.includes(i)) groups.push([i]);
  });
  groups.sort((a, b) => Math.min(...a) - Math.min(...b));
  return { groups, orphanHeaders };
}

// --------------------------------------------------------------------------- resume records

const STORAGE_PREFIX = 'hsl.upload.v1.';

export interface FileFingerprint {
  name: string;
  size: number;
  lastModified: number;
}

export function fingerprint(file: Pick<File, 'name' | 'size' | 'lastModified'>): FileFingerprint {
  return { name: file.name, size: file.size, lastModified: file.lastModified };
}

function sameFingerprint(a: FileFingerprint, b: FileFingerprint): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

/** What survives a page reload (no SAS URLs are stored). */
export interface StoredUpload {
  version: 1;
  id: string;
  projectId: number;
  strategy: UploadStrategy;
  blockSize: number;
  expiresAt: string;
  createdAt: string;
  options: UploadOptions;
  files: (FileFingerprint & { idx: number; nBlocks: number })[];
}

function defaultStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readRecord(storage: Storage, key: string): StoredUpload | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const record = JSON.parse(raw) as StoredUpload;
    return record.version === 1 && typeof record.id === 'string' && Array.isArray(record.files)
      ? record
      : null;
  } catch {
    return null;
  }
}

/** Interrupted uploads of a project kept for resuming (expired ones are dropped). */
export function listStoredUploads(
  projectId: number,
  storage: Storage | null = defaultStorage(),
  now = Date.now(),
): StoredUpload[] {
  if (!storage) return [];
  const out: StoredUpload[] = [];
  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i);
    if (!key?.startsWith(STORAGE_PREFIX)) continue;
    const record = readRecord(storage, key);
    if (!record || Date.parse(record.expiresAt) <= now) {
      storage.removeItem(key);
      continue;
    }
    if (record.projectId === projectId) out.push(record);
  }
  return out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

const listeners = new Set<() => void>();

/** Notifies {@link subscribeStoredUploads} listeners (same tab; other tabs get `storage` events). */
export function notifyStoredUploads(): void {
  for (const listener of listeners) listener();
}

/** Subscribes to changes of the resume records (this tab and other tabs). */
export function subscribeStoredUploads(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(STORAGE_PREFIX)) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function saveStoredUpload(record: StoredUpload, storage: Storage | null = defaultStorage()): void {
  try {
    storage?.setItem(STORAGE_PREFIX + record.id, JSON.stringify(record));
  } catch {
    // quota / private mode: resuming is best effort
  }
  notifyStoredUploads();
}

export function removeStoredUpload(uploadId: string, storage: Storage | null = defaultStorage()): void {
  try {
    storage?.removeItem(STORAGE_PREFIX + uploadId);
  } catch {
    // ignore
  }
  notifyStoredUploads();
}

/**
 * The stored upload whose files are exactly `files` (same names, sizes, mtimes), with the
 * selected `File` for each stored file index.
 */
export function matchStoredUpload(
  records: readonly StoredUpload[],
  files: readonly File[],
): { record: StoredUpload; byIdx: Map<number, File> } | null {
  for (const record of records) {
    if (record.files.length !== files.length) continue;
    const byIdx = new Map<number, File>();
    const free = [...files];
    for (const stored of record.files) {
      const k = free.findIndex((f) => sameFingerprint(fingerprint(f), stored));
      if (k < 0) break;
      byIdx.set(stored.idx, free[k]);
      free.splice(k, 1);
    }
    if (byIdx.size === record.files.length) return { record, byIdx };
  }
  return null;
}

/** Discards an interrupted upload (server data and local record); errors are ignored. */
export async function discardStoredUpload(
  uploadId: string,
  storage: Storage | null = defaultStorage(),
): Promise<void> {
  removeStoredUpload(uploadId, storage);
  try {
    await api.delete(`/api/uploads/${uploadId}`);
  } catch {
    // already gone / completed
  }
}

// --------------------------------------------------------------------------- progress

export type UploadPhase = 'preparing' | 'uploading' | 'completing' | 'done' | 'failed' | 'aborted' | 'paused';
export type UploadFileStatus = 'pending' | 'uploading' | 'done' | 'failed';

export interface UploadFileProgress {
  idx: number;
  name: string;
  size: number;
  /** Bytes on the server (incl. blocks found when resuming). */
  uploaded: number;
  nBlocks: number;
  blocksDone: number;
  status: UploadFileStatus;
  /** Bytes per second over the last seconds (0 when idle). */
  rate: number;
  /** Seconds left at the current rate, `null` when unknown. */
  eta: number | null;
  error: string | null;
}

export interface UploadProgress {
  uploadId: string | null;
  strategy: UploadStrategy | null;
  phase: UploadPhase;
  files: UploadFileProgress[];
  uploaded: number;
  total: number;
  rate: number;
  eta: number | null;
  /** Bytes that were already on the server when a resumed upload started. */
  resumedBytes: number;
  /** Retries so far (all blocks). */
  retries: number;
  error: string | null;
}

const RATE_WINDOW_MS = 8000;

class RateMeter {
  private samples: { t: number; bytes: number }[] = [];

  add(t: number, bytes: number): void {
    this.samples.push({ t, bytes });
    this.trim(t);
  }

  rate(t: number, since: number): number {
    this.trim(t);
    if (this.samples.length === 0) return 0;
    const span = Math.max(t - Math.max(since, t - RATE_WINDOW_MS), 250);
    const bytes = this.samples.reduce((n, s) => n + s.bytes, 0);
    return (bytes * 1000) / span;
  }

  private trim(t: number): void {
    while (this.samples.length > 0 && this.samples[0].t < t - RATE_WINDOW_MS) this.samples.shift();
  }
}

// --------------------------------------------------------------------------- errors

export class UploadAbortedError extends Error {
  constructor(message = 'Upload cancelled') {
    super(message);
    this.name = 'UploadAbortedError';
  }
}

export class UploadPausedError extends Error {
  constructor() {
    super('Upload paused');
    this.name = 'UploadPausedError';
  }
}

/** A failed PUT to Azure Blob Storage. */
export class BlobPutError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.name = 'BlobPutError';
    this.status = status;
    this.code = code;
  }
}

const RETRY_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Whether a failed block request is worth retrying. */
export function isRetryable(error: unknown): boolean {
  if (isApiError(error)) return error.status === 0 || RETRY_STATUSES.has(error.status);
  if (error instanceof BlobPutError)
    return error.status === 0 || error.status === 403 || RETRY_STATUSES.has(error.status);
  return error instanceof TypeError; // fetch network failure
}

/** Exponential backoff with jitter: 0.5 s, 1 s, 2 s … capped at 15 s. */
export function defaultRetryDelay(attempt: number): number {
  const base = Math.min(15_000, 500 * 2 ** (attempt - 1));
  return base * (0.75 + Math.random() * 0.5);
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** `se=` (signed expiry) of a SAS URL in ms since the epoch, if present. */
export function sasExpiry(url: string): number | null {
  try {
    const se = new URL(url).searchParams.get('se');
    const t = se ? Date.parse(se) : NaN;
    return Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------- task

export interface UploadTaskOptions {
  projectId: number;
  files: readonly File[];
  options?: UploadOptions;
  /** Parallel block requests (default 4). */
  concurrency?: number;
  /** Attempts per block before the upload fails (default 6). */
  maxAttempts?: number;
  retryDelay?: (attempt: number) => number;
  /** Resume records (default `localStorage`; `null` disables resuming). */
  storage?: Storage | null;
  /** Resume this stored upload (`byIdx`: file per stored index), see {@link matchStoredUpload}. */
  resume?: { record: StoredUpload; byIdx: Map<number, File> } | null;
  onProgress?: (progress: UploadProgress) => void;
  /** Clock (tests). */
  now?: () => number;
}

interface FileState {
  idx: number;
  file: File;
  nBlocks: number;
  putUrl: string | null;
  sasRefresh: Promise<void> | null;
  meter: RateMeter;
  startedAt: number;
}

const SAS_REFRESH_MARGIN_MS = 2 * 60_000;

/**
 * One upload (one `UploadSession`). Starts immediately; `promise` resolves with the
 * `/complete` result or rejects ({@link UploadAbortedError}, {@link UploadPausedError},
 * `ApiError`, {@link BlobPutError}).
 */
export class UploadTask {
  readonly promise: Promise<UploadResult>;
  private readonly opts: {
    projectId: number;
    options: UploadOptions;
    concurrency: number;
    maxAttempts: number;
    retryDelay: (attempt: number) => number;
    storage: Storage | null;
    resume: UploadTaskOptions['resume'];
    onProgress: UploadTaskOptions['onProgress'];
    now: () => number;
  };
  private readonly controller = new AbortController();
  /** Aborted on the first block failure: cancels retry back-off waits, not requests. */
  private readonly failed = new AbortController();
  private stopReason: 'abort' | 'pause' | null = null;
  private progress: UploadProgress;
  private session: { id: string; strategy: UploadStrategy; blockSize: number } | null = null;
  private files: FileState[] = [];
  private totalMeter = new RateMeter();
  private startedAt = 0;

  constructor(options: UploadTaskOptions) {
    this.opts = {
      projectId: options.projectId,
      options: options.options ?? {},
      concurrency: options.concurrency ?? 4,
      maxAttempts: options.maxAttempts ?? 6,
      retryDelay: options.retryDelay ?? defaultRetryDelay,
      storage: options.storage === undefined ? defaultStorage() : options.storage,
      resume: options.resume ?? null,
      onProgress: options.onProgress,
      now: options.now ?? (() => Date.now()),
    };
    const resume = options.resume;
    const source = resume
      ? resume.record.files.map((f) => ({ idx: f.idx, file: resume.byIdx.get(f.idx)! }))
      : options.files.map((file, idx) => ({ idx, file }));
    this.progress = {
      uploadId: options.resume?.record.id ?? null,
      strategy: options.resume?.record.strategy ?? null,
      phase: 'preparing',
      files: source.map(({ idx, file }) => ({
        idx,
        name: file.name,
        size: file.size,
        uploaded: 0,
        nBlocks: 0,
        blocksDone: 0,
        status: 'pending',
        rate: 0,
        eta: null,
        error: null,
      })),
      uploaded: 0,
      total: source.reduce((n, { file }) => n + file.size, 0),
      rate: 0,
      eta: null,
      resumedBytes: 0,
      retries: 0,
      error: null,
    };
    this.promise = this.run(source);
    // callers may only observe progress; never leave an unhandled rejection behind
    this.promise.catch(() => undefined);
  }

  getProgress(): UploadProgress {
    return this.progress;
  }

  /** Cancels in-flight requests and deletes the upload on the server. */
  abort(): void {
    if (this.stopReason) return;
    this.stopReason = 'abort';
    this.controller.abort();
  }

  /** Stops sending but keeps the server session and the resume record. */
  pause(): void {
    if (this.stopReason) return;
    this.stopReason = 'pause';
    this.controller.abort();
  }

  // ------------------------------------------------------------------ internals

  private emit(): void {
    const t = this.opts.now();
    const files = this.progress.files.map((fp) => {
      const state = this.files.find((f) => f.idx === fp.idx);
      const rate = state && fp.status === 'uploading' ? state.meter.rate(t, state.startedAt) : 0;
      const left = fp.size - fp.uploaded;
      return { ...fp, rate, eta: fp.status === 'done' ? 0 : rate > 0 ? left / rate : null };
    });
    const uploaded = files.reduce((n, f) => n + f.uploaded, 0);
    const active = this.progress.phase === 'uploading';
    const rate = active ? this.totalMeter.rate(t, this.startedAt) : 0;
    const left = this.progress.total - uploaded;
    this.progress = {
      ...this.progress,
      files,
      uploaded,
      rate,
      eta: left <= 0 ? 0 : rate > 0 ? left / rate : null,
    };
    this.opts.onProgress?.(this.progress);
  }

  private patchFile(idx: number, patch: Partial<UploadFileProgress>): void {
    this.progress = {
      ...this.progress,
      files: this.progress.files.map((f) => (f.idx === idx ? { ...f, ...patch } : f)),
    };
  }

  private setPhase(phase: UploadPhase, error: string | null = null): void {
    this.progress = { ...this.progress, phase, error };
    this.emit();
  }

  private checkStopped(): void {
    if (this.stopReason === 'abort') throw new UploadAbortedError();
    if (this.stopReason === 'pause') throw new UploadPausedError();
  }

  private async run(source: { idx: number; file: File }[]): Promise<UploadResult> {
    try {
      const done = await this.prepare(source);
      this.checkStopped();
      this.startedAt = this.opts.now();
      this.setPhase('uploading');
      await this.sendBlocks(done);
      this.checkStopped();
      this.setPhase('completing');
      const result = await this.complete();
      removeStoredUpload(this.session!.id, this.opts.storage);
      this.progress = {
        ...this.progress,
        files: this.progress.files.map((f) => ({ ...f, status: 'done', uploaded: f.size })),
      };
      this.setPhase('done');
      return result;
    } catch (raw) {
      const error = this.stopReason
        ? this.stopReason === 'abort'
          ? new UploadAbortedError()
          : new UploadPausedError()
        : raw;
      if (error instanceof UploadAbortedError) {
        if (this.session) await discardStoredUpload(this.session.id, this.opts.storage);
        this.setPhase('aborted');
      } else if (error instanceof UploadPausedError) {
        this.setPhase('paused');
      } else {
        this.setPhase('failed', error instanceof Error ? error.message : String(error));
      }
      throw error;
    }
  }

  /** Creates (or re-opens) the session; returns the block ids already on the server. */
  private async prepare(source: { idx: number; file: File }[]): Promise<Map<number, Set<string>>> {
    const signal = this.controller.signal;
    const done = new Map<number, Set<string>>();
    const resume = this.opts.resume;
    if (resume) {
      const { record } = resume;
      this.session = { id: record.id, strategy: record.strategy, blockSize: record.blockSize };
      this.files = source.map(({ idx, file }) => ({
        idx,
        file,
        nBlocks: record.files.find((f) => f.idx === idx)?.nBlocks ?? blockCount(file.size, record.blockSize),
        putUrl: null,
        sasRefresh: null,
        meter: new RateMeter(),
        startedAt: 0,
      }));
      for (const state of this.files) {
        const { uncommitted } = await api.get<{ uncommitted: string[] }>(
          `/api/uploads/${record.id}/files/${state.idx}/blocks`,
          { signal },
        );
        const valid = new Set(blockIds(state.nBlocks));
        done.set(state.idx, new Set(uncommitted.filter((id) => valid.has(id))));
        if (record.strategy === 'azure') await this.refreshSas(state);
      }
    } else {
      const session = await api.post<UploadSession>(
        `/api/projects/${this.opts.projectId}/uploads`,
        {
          files: source.map(({ file }) => ({ name: file.name, size: file.size })),
          options: this.opts.options,
        },
        { signal },
      );
      this.session = { id: session.id, strategy: session.strategy, blockSize: session.block_size };
      this.files = source.map(({ idx, file }) => {
        const info = session.files.find((f) => f.idx === idx);
        return {
          idx,
          file,
          nBlocks: info?.n_blocks ?? blockCount(file.size, session.block_size),
          putUrl: info?.put_url ?? null,
          sasRefresh: null,
          meter: new RateMeter(),
          startedAt: 0,
        };
      });
      for (const state of this.files) done.set(state.idx, new Set());
      saveStoredUpload(
        {
          version: 1,
          id: session.id,
          projectId: this.opts.projectId,
          strategy: session.strategy,
          blockSize: session.block_size,
          expiresAt: session.expires_at,
          createdAt: new Date(this.opts.now()).toISOString(),
          options: this.opts.options,
          files: this.files.map((f) => ({ ...fingerprint(f.file), idx: f.idx, nBlocks: f.nBlocks })),
        },
        this.opts.storage,
      );
    }
    let resumedBytes = 0;
    for (const state of this.files) {
      const have = done.get(state.idx)!;
      let bytes = 0;
      for (let n = 0; n < state.nBlocks; n++) {
        if (have.has(blockId(n))) {
          const [a, b] = blockRange(n, state.file.size, this.session.blockSize);
          bytes += b - a;
        }
      }
      resumedBytes += bytes;
      this.patchFile(state.idx, {
        nBlocks: state.nBlocks,
        blocksDone: have.size,
        uploaded: bytes,
        status: have.size >= state.nBlocks ? 'done' : 'pending',
      });
    }
    this.progress = {
      ...this.progress,
      uploadId: this.session.id,
      strategy: this.session.strategy,
      resumedBytes,
    };
    this.emit();
    return done;
  }

  private async refreshSas(state: FileState): Promise<void> {
    state.sasRefresh ??= (async () => {
      try {
        const { put_url } = await api.post<{ put_url: string }>(
          `/api/uploads/${this.session!.id}/files/${state.idx}/sas`,
          undefined,
          { signal: this.controller.signal },
        );
        state.putUrl = put_url;
      } finally {
        state.sasRefresh = null;
      }
    })();
    return state.sasRefresh;
  }

  private async sendBlocks(done: Map<number, Set<string>>): Promise<void> {
    const queue: { state: FileState; n: number }[] = [];
    for (const state of this.files) {
      const have = done.get(state.idx)!;
      for (let n = 0; n < state.nBlocks; n++) if (!have.has(blockId(n))) queue.push({ state, n });
    }
    const remaining = new Map(this.files.map((f) => [f.idx, queue.filter((q) => q.state === f).length]));
    const outcome: { failure: Error | null } = { failure: null };
    const worker = async () => {
      for (;;) {
        if (outcome.failure !== null || this.stopReason) return;
        const item = queue.shift();
        if (!item) return;
        const { state, n } = item;
        if (state.startedAt === 0) {
          state.startedAt = this.opts.now();
          this.patchFile(state.idx, { status: 'uploading' });
          this.emit();
        }
        try {
          const bytes = await this.sendWithRetry(state, n);
          const t = this.opts.now();
          state.meter.add(t, bytes);
          this.totalMeter.add(t, bytes);
          const left = remaining.get(state.idx)! - 1;
          remaining.set(state.idx, left);
          const fp = this.progress.files.find((f) => f.idx === state.idx)!;
          this.patchFile(state.idx, {
            uploaded: fp.uploaded + bytes,
            blocksDone: fp.blocksDone + 1,
            status: fp.status === 'failed' ? 'failed' : left === 0 ? 'done' : 'uploading',
          });
          this.emit();
        } catch (error) {
          if (outcome.failure === null && !this.stopReason) {
            outcome.failure = error instanceof Error ? error : new Error('Block upload failed');
            this.patchFile(state.idx, {
              status: 'failed',
              error: error instanceof Error ? error.message : String(error),
            });
            // let in-flight blocks finish (they count when resuming), stop retries and new blocks
            this.failed.abort();
          }
          return;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.max(1, this.opts.concurrency) }, worker));
    this.checkStopped();
    if (outcome.failure !== null) throw outcome.failure;
  }

  private async sendWithRetry(state: FileState, n: number): Promise<number> {
    const signal = this.controller.signal;
    const [start, end] = blockRange(n, state.file.size, this.session!.blockSize);
    // a Blob slice is sent as-is: the browser streams it from disk, nothing is copied on the
    // main thread (8 MiB blocks × 4 in parallel used to jank the page)
    const block = state.file.slice(start, end);
    for (let attempt = 1; ; attempt++) {
      try {
        await this.sendBlock(state, n, block);
        return end - start;
      } catch (error) {
        if (isAbort(error) || this.stopReason || this.failed.signal.aborted) throw error;
        if (!isRetryable(error) || attempt >= this.opts.maxAttempts) throw error;
        this.progress = { ...this.progress, retries: this.progress.retries + 1 };
        if (error instanceof BlobPutError && error.status === 403) await this.refreshSas(state);
        const wait = isBusyError(error) ? busyRetryDelayMs(error, attempt) : this.opts.retryDelay(attempt);
        await sleep(wait, AbortSignal.any([signal, this.failed.signal]));
      }
    }
  }

  private async sendBlock(state: FileState, n: number, block: Blob): Promise<void> {
    const signal = this.controller.signal;
    const id = blockId(n);
    if (this.session!.strategy === 'local') {
      await api.put(`/api/uploads/${this.session!.id}/files/${state.idx}/blocks/${n}`, block, {
        signal,
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      return;
    }
    if (state.sasRefresh) await state.sasRefresh;
    const expiry = state.putUrl ? sasExpiry(state.putUrl) : null;
    if (!state.putUrl || (expiry !== null && expiry - this.opts.now() < SAS_REFRESH_MARGIN_MS)) {
      await this.refreshSas(state);
    }
    let response: Response;
    try {
      response = await fetch(`${state.putUrl}&comp=block&blockid=${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: block,
        signal,
        credentials: 'omit',
      });
    } catch (error) {
      if (isAbort(error)) throw error;
      throw new BlobPutError(0, 'network_error', 'Cannot reach the blob storage.');
    }
    if (!response.ok) {
      const code = response.headers.get('x-ms-error-code');
      throw new BlobPutError(
        response.status,
        code,
        `Blob storage rejected block ${n} of ${state.file.name}: HTTP ${response.status}${code ? ` (${code})` : ''}`,
      );
    }
  }

  private async complete(): Promise<UploadResult> {
    const body = {
      files: this.files.map((state) => ({ idx: state.idx, block_ids: blockIds(state.nBlocks) })),
    };
    for (let attempt = 1; ; attempt++) {
      try {
        return await api.post<UploadResult>(`/api/uploads/${this.session!.id}/complete`, body, {
          signal: this.controller.signal,
        });
      } catch (error) {
        if (isAbort(error) || this.stopReason) throw error;
        const transient = isApiError(error) && (error.status === 0 || error.status >= 502);
        if (!transient || attempt >= 3) throw error;
        await sleep(this.opts.retryDelay(attempt), this.controller.signal);
      }
    }
  }
}

/** Human-readable message for any upload failure. */
export function uploadErrorMessage(error: unknown): string {
  if (error instanceof UploadAbortedError || error instanceof UploadPausedError) return error.message;
  if (error instanceof ApiError) {
    if (error.code === 'upload_expired') return 'The upload session expired. Start the upload again.';
    return error.detail;
  }
  return error instanceof Error ? error.message : String(error);
}
